import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import LocationPicker from "@/components/LocationPicker";
import RoadrunnerAlert from "@/components/RoadrunnerAlert";
import DriverDocumentsPanel from "@/components/DriverDocumentsPanel";
import DriverGettingStarted from "@/components/DriverGettingStarted";
import DriverTierCard from "@/components/DriverTierCard";
import StripeConnectPanel from "@/components/StripeConnectPanel";
import ZoneAndCardPanel from "@/components/ZoneAndCardPanel";
import VehicleSetupPanel from "@/components/VehicleSetupPanel";
import SupportDrawer from "@/components/SupportDrawer";
import InJobChat from "@/components/InJobChat";
import TutorialOverlay from "@/components/TutorialOverlay";
import MobileHelpFab from "@/components/MobileHelpFab";
import { LogOut, Zap, Phone, MapPin, CheckCircle2, Navigation, Volume2, LifeBuoy, AlertCircle, UserRound, TrendingUp } from "lucide-react";

const STATUS_FLOW = ["accepted", "en_route", "on_scene", "completed"];

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [online, setOnline] = useState(user?.online || false);
  const [pending, setPending] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [alertTrigger, setAlertTrigger] = useState(0);
  const knownIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const [permPush, setPermPush] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [supportOpen, setSupportOpen] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [tutorialOpen, setTutorialOpen] = useState(false);

  useEffect(() => {
    const tick = async () => {
      try {
        if (online) {
          const { data } = await api.get("/bookings/pending");
          // New booking detection
          const newOnes = data.filter((b) => !knownIdsRef.current.has(b.id));
          if (!firstLoadRef.current && newOnes.length > 0) {
            setAlertTrigger((n) => n + 1);
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              try {
                new Notification("🚨 New ASAP Request!", { body: `${newOnes[0].service_name} · $${newOnes[0].price}`, tag: newOnes[0].id });
              } catch {}
            }
          }
          firstLoadRef.current = false;
          data.forEach((b) => knownIdsRef.current.add(b.id));
          setPending(data);
        } else {
          setPending([]);
        }
        const mine = await api.get("/bookings/my");
        setMyJobs(mine.data);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => clearInterval(id);
  }, [online]);

  // GPS ping every 20s while ONLINE only — enables customer ETA display + live tracking map.
  // App Store / Play Store compliance: location is used only for dispatching the driver to the
  // nearest customer and displaying live ETA to the customer waiting on rescue. The permission
  // prompt is triggered by the user's explicit "Go Online" toggle — never on mount.
  useEffect(() => {
    if (!online || typeof navigator === "undefined" || !navigator.geolocation) return;
    const ping = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          api.post("/driver/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: false, maximumAge: 30000, timeout: 8000 }
      );
    };
    ping();
    const t = setInterval(ping, 20000);
    return () => clearInterval(t);
  }, [online]);

  const toggleOnline = async () => {
    if (!online) {
      // Vehicle setup gate
      if (!user?.vehicle_category || !(user?.service_capabilities || []).length) {
        setBlockReason("Set your vehicle type and services below before going online.");
        setTimeout(() => setBlockReason(""), 6000);
        return;
      }
      try {
        const { data } = await api.post("/driver/online-check");
        if (!data.can_go_online) {
          setBlockReason(data.message || "Cannot go online.");
          setTimeout(() => setBlockReason(""), 5000);
          return;
        }
      } catch {}
    }
    const v = !online;
    setOnline(v);
    firstLoadRef.current = true;
    knownIdsRef.current = new Set();
    try {
      await api.post("/driver/online", { online: v });
    } catch {}
  };

  const askPush = async () => {
    if (typeof Notification === "undefined") return;
    const r = await Notification.requestPermission();
    setPermPush(r);
  };

  const accept = async (id) => {
    try {
      await api.post(`/bookings/${id}/accept`);
      setPending((p) => p.filter((b) => b.id !== id));
      // Optimistic refresh so advance button appears immediately
      try {
        const mine = await api.get("/bookings/my");
        setMyJobs(mine.data);
      } catch {}
    } catch {}
  };
  const updateStatus = async (id, status) => {
    try {
      await api.post(`/bookings/${id}/status`, { status });
    } catch {}
  };

  const doLogout = async () => { await logout(); nav("/"); };
  const activeJobs = myJobs.filter((j) => ["accepted", "en_route", "on_scene"].includes(j.status));
  const completedJobs = myJobs.filter((j) => j.status === "completed");

  return (
    <div className="driver-theme" data-testid="driver-dashboard">
      <MobileHelpFab requestPath="/customer" requestLabel="Request as Customer" />
      <TutorialOverlay role="driver" forceOpen={tutorialOpen} onClose={() => setTutorialOpen(false)} />
      <RoadrunnerAlert trigger={alertTrigger} />

      <header className="bg-asap-deep border-b-2 border-asap-blue sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
          <Logo size={88} dark />
          <div className="flex items-center gap-3">
            <button onClick={toggleOnline} data-testid="online-toggle"
              className={`px-4 py-2 font-display uppercase text-sm border-2 transition-all ${online ? "bg-asap-yellow text-asap-deep border-asap-yellow shadow-hard-sm" : "bg-transparent text-asap-yellow border-asap-yellow/50"}`}>
              <span className="inline-flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-500 pulse-dot" : "bg-slate-500"}`}/>
                {online ? "Online" : "Offline"}
              </span>
            </button>
            <button onClick={() => nav("/customer")} data-testid="request-service-btn"
              className="px-3 py-2 bg-white text-asap-deep border-2 border-white text-xs font-bold uppercase inline-flex items-center gap-2 hover:bg-asap-yellow hover:border-asap-yellow transition">
              <UserRound size={14}/> Request Service
            </button>
            {permPush !== "granted" && (
              <button data-testid="enable-push-btn" onClick={askPush} className="px-3 py-2 border-2 border-asap-yellow/40 text-asap-yellow text-xs font-bold uppercase hover:bg-asap-yellow/10 inline-flex items-center gap-2">
                <Volume2 size={14}/> Enable Alerts
              </button>
            )}
            <button onClick={() => setSupportOpen(true)} data-testid="driver-support" className="px-3 py-2 border-2 border-asap-yellow/60 text-asap-yellow text-xs font-bold uppercase hover:bg-asap-yellow/10 inline-flex items-center gap-2">
              <LifeBuoy size={14}/> Help
            </button>
            <button onClick={() => setTutorialOpen(true)} data-testid="driver-tutorial" className="inline-flex items-center gap-1 px-2 sm:px-3 py-2 border-2 border-asap-yellow/60 text-asap-yellow text-xs font-bold uppercase hover:bg-asap-yellow/10">
              Tutorial
            </button>
            <a href="tel:+18317477141" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 border-2 border-white/30 text-white text-xs font-bold uppercase hover:bg-white/10"><Phone size={14}/> 831-747-7141</a>
            <button onClick={() => nav("/account")} data-testid="driver-account" title="Account settings" className="px-2 sm:px-3 py-2 border-2 border-white/30 text-white hover:bg-white/10 text-xs sm:text-sm font-bold uppercase inline-flex items-center gap-1"><UserRound size={14}/><span className="hidden sm:inline"> Account</span></button>
            <button data-testid="driver-logout" onClick={doLogout} className="px-3 py-2 border-2 border-white/30 text-white hover:bg-white/10 text-sm font-bold uppercase inline-flex items-center gap-2"><LogOut size={14}/> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-6">
          <DriverGettingStarted pendingCount={pending.length} activeCount={activeJobs.length}/>
          {blockReason && (
            <div className="bg-asap-red/30 border-2 border-asap-red text-white p-4 flex items-start gap-3" data-testid="block-toast">
              <AlertCircle size={20} className="text-asap-yellow shrink-0 mt-0.5"/>
              <div>
                <div className="font-bold uppercase text-asap-yellow text-sm">Can't go online</div>
                <div className="text-sm mt-1">{blockReason}</div>
              </div>
            </div>
          )}
          <div className="bg-asap-blue/30 backdrop-blur-md border border-asap-blue p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-asap-yellow font-bold">Incoming</div>
                <div className="font-display text-2xl uppercase">Pending requests</div>
              </div>
              <div className="font-display text-4xl text-asap-yellow" data-testid="pending-count">{pending.length}</div>
            </div>
          </div>

          {!online && (
            <div className="bg-asap-blue/20 border border-dashed border-asap-yellow/40 p-6 text-center">
              <Zap size={28} className="text-asap-yellow mx-auto"/>
              <p className="mt-2 text-white/70">You're offline. Flip <span className="text-asap-yellow font-bold">Online</span> to receive requests.</p>
            </div>
          )}

          {online && pending.length === 0 && (
            <div className="bg-asap-blue/20 border border-asap-blue p-6 text-center text-white/60">No pending requests. The roadrunner is on standby…</div>
          )}

          <div className="space-y-3" data-testid="pending-list">
            {pending.map((b) => (
              <PendingRequestCard key={b.id} booking={b} onAccept={accept} />
            ))}
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-asap-yellow font-bold mb-3">Your active jobs</div>
            <div className="space-y-4">
              {activeJobs.length === 0 && <div className="text-white/40 text-sm">No active jobs yet.</div>}
              {activeJobs.map((b) => (
                <ActiveJobCard key={b.id} booking={b} onUpdate={updateStatus} />
              ))}
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <DriverTierCard />
          <StripeConnectPanel />
          <DriverDocumentsPanel approvalStatus={user?.approval_status}/>
          <VehicleSetupPanel />
          <ZoneAndCardPanel />
          <div className="bg-asap-blue/30 border border-asap-blue p-5">
            <div className="text-xs uppercase tracking-[0.3em] text-asap-yellow font-bold">Today</div>
            <div className="font-display text-4xl mt-2">{completedJobs.length}</div>
            <div className="text-xs text-white/60 uppercase tracking-widest">Completed jobs</div>
          </div>
          <div className="bg-asap-blue/30 border border-asap-blue p-5">
            <div className="text-xs uppercase tracking-[0.3em] text-asap-yellow font-bold">Recent</div>
            <div className="mt-3 space-y-2 max-h-80 overflow-y-auto" data-testid="driver-recent-list">
              {completedJobs.slice(0, 8).map((b) => (
                <div key={b.id} className="flex justify-between text-sm border-b border-white/10 pb-2">
                  <div className="text-white/80">{b.service_name}</div>
                  <div className="text-asap-yellow font-display">${b.price.toFixed(2)}</div>
                </div>
              ))}
              {completedJobs.length === 0 && <div className="text-white/40 text-xs">No history yet.</div>}
            </div>
          </div>
        </aside>
      </main>
      <SupportDrawer open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

function PendingRequestCard({ booking, onAccept }) {
  const { user } = useAuth();
  // Auto-decline countdown: 45s. Visual only for now — request stays in queue until any driver accepts.
  const [secondsLeft, setSecondsLeft] = useState(45);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    setSecondsLeft(45);
    setExpired(false);
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { setExpired(true); clearInterval(t); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [booking.id]);

  const pct = Math.max(0, (secondsLeft / 45) * 100);
  const nav = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${booking.pickup_lat},${booking.pickup_lng}&travelmode=driving`;
    window.open(url, "_blank");
  };

  // Show driver's take: 70% base × tier multiplier + 100% of tip (tip unknown pre-accept, so show base take)
  const multiplier = user?.tier_multiplier || 1.0;
  const yourTake = (booking.price * 0.70 * multiplier).toFixed(2);

  return (
    <div className={`bg-gradient-to-r from-asap-blue/60 to-asap-blue/20 border-2 ${expired ? "border-white/30 opacity-70" : "border-asap-yellow/40"} p-5`} data-testid={`pending-${booking.id}`}>
      <div className="flex justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="font-display text-xl text-asap-yellow uppercase">{booking.service_name}</div>
            <div className="text-white/60 text-xs line-through">${booking.price.toFixed(2)}</div>
          </div>
          <div className="mt-1 inline-flex items-center gap-2 px-2 py-1 bg-asap-yellow text-asap-deep font-display text-sm border-2 border-asap-yellow" data-testid={`your-take-${booking.id}`}>
            You earn <span className="font-bold">${yourTake}</span> <span className="text-[9px] uppercase tracking-widest">+100% tips</span>
          </div>
          <div className="text-sm text-white/70 mt-2 flex items-center gap-1"><MapPin size={14}/> {booking.pickup_address}</div>
          <div className="text-sm text-white/70 mt-1">{booking.vehicle_info || "Vehicle info not provided"}</div>
          {booking.notes && <div className="text-xs text-white/60 mt-1 italic">"{booking.notes}"</div>}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <button data-testid={`accept-${booking.id}`} onClick={() => onAccept(booking.id)} disabled={expired} className={`px-6 py-3 font-display uppercase border-2 shadow-[4px_4px_0_0_#FFD700] transition-all ${expired ? "bg-slate-500 border-slate-500 text-white/60 cursor-not-allowed" : "bg-asap-yellow text-asap-deep border-asap-yellow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none"}`}>
            {expired ? "Missed" : "Accept →"}
          </button>
          <button onClick={nav} data-testid={`nav-${booking.id}`} className="inline-flex items-center gap-1 text-[10px] text-asap-yellow border border-asap-yellow/60 px-2 py-1 hover:bg-asap-yellow/10 uppercase tracking-widest font-bold">
            <Navigation size={11}/> Preview route
          </button>
        </div>
      </div>
      {!expired && (
        <div className="mt-3">
          <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/60 font-bold">
            <span>Auto-release in</span>
            <span className="text-asap-yellow" data-testid={`countdown-${booking.id}`}>{secondsLeft}s</span>
          </div>
          <div className="mt-1 h-1.5 bg-black/30 rounded">
            <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: secondsLeft > 10 ? "#FFD700" : "#E62E2D" }}/>
          </div>
        </div>
      )}
    </div>
  );
}

function ActiveJobCard({ booking, onUpdate }) {
  const idx = STATUS_FLOW.indexOf(booking.status);
  const next = idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  return (
    <div className="bg-asap-blue/40 backdrop-blur-md border border-asap-blue p-5" data-testid={`active-${booking.id}`}>
      <div className="flex justify-between gap-4 items-start">
        <div className="flex-1">
          <div className="font-display text-xl text-asap-yellow uppercase">{booking.service_name}</div>
          <div className="text-sm text-white/70 mt-1 flex items-center gap-1"><MapPin size={14}/> {booking.pickup_address}</div>
          <div className="text-xs text-white/60 mt-1 flex items-center gap-1"><Phone size={12}/> {booking.customer_name} · {booking.customer_phone || "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-asap-yellow font-bold">{booking.status.replace("_", " ")}</div>
          <div className="font-display text-asap-yellow text-2xl mt-1">${booking.price.toFixed(2)}</div>
        </div>
      </div>

      <div className="mt-3 h-44 -mx-5 -mb-5 border-t border-asap-blue">
        <LocationPicker value={{ lat: booking.pickup_lat, lng: booking.pickup_lng }} readOnly height={176} />
      </div>

      <div className="mt-3 -mx-5 -mb-5 border-t border-asap-blue bg-white">
        <InJobChat bookingId={booking.id} otherName={booking.customer_name}/>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        {next && (
          <button data-testid={`advance-${booking.id}`} onClick={() => onUpdate(booking.id, next)} className="px-4 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-sm border-2 border-asap-yellow hover:bg-asap-orange hover:border-asap-orange transition inline-flex items-center gap-2">
            <CheckCircle2 size={14}/> Mark {next.replace("_", " ")}
          </button>
        )}
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${booking.pickup_lat},${booking.pickup_lng}&travelmode=driving`}
          target="_blank"
          rel="noreferrer"
          data-testid={`navigate-${booking.id}`}
          className="px-4 py-2 bg-asap-deep text-white font-display uppercase text-sm border-2 border-asap-deep hover:bg-asap-blue transition inline-flex items-center gap-2"
        >
          <Navigation size={14}/> Navigate
        </a>
        {booking.customer_phone && (
          <a
            href={`tel:${booking.customer_phone}`}
            data-testid={`call-customer-${booking.id}`}
            className="px-4 py-2 border-2 border-asap-yellow text-asap-yellow font-display uppercase text-sm hover:bg-asap-yellow hover:text-asap-deep transition inline-flex items-center gap-2"
          >
            <Phone size={14}/> Call
          </a>
        )}
        {booking.status === "completed" && (
          <div className="px-4 py-2 bg-emerald-600 text-white font-bold uppercase text-sm inline-flex items-center gap-2"><CheckCircle2 size={14}/> Done</div>
        )}
      </div>
    </div>
  );
}
